import { HttpErrorResponse } from '@angular/common/http';

/**
 * Network error classification matching mobile's `NetworkErrorType` enum.
 *
 * Mobile reference: `lib/modules/errors/network_error_type.dart`
 */
export enum NetworkErrorType {
  /** Browser could not reach the server at all (status 0, offline). */
  NoInternet = 'noInternet',
  /** Server did not respond in time (408, 504, connect/receive timeout). */
  Timeout = 'timeout',
  /** Server explicitly reports maintenance (503 Service Unavailable). */
  Maintenance = 'maintenance',
  /** Any other error (400, 401, 403, 500, 502, unrecognised). */
  Unknown = 'unknown',
}

/**
 * Classify an `HttpErrorResponse` into a `NetworkErrorType`.
 *
 * Port of mobile's `NetworkException.fromDioException()` with
 * `treatServerErrorsAsTimeout: false` (the setting GTN uses).
 *
 * The flag mirrors mobile: when `false`, status >= 500 (except 503/504)
 * maps to `Unknown` rather than `Timeout`. GTN always uses `false`.
 */
export function classifyHttpError(
  error: HttpErrorResponse,
  treatServerErrorsAsTimeout = false,
): NetworkErrorType {
  // status === 0: browser-level failure (CORS, DNS, offline, network unreachable)
  if (error.status === 0) {
    return NetworkErrorType.NoInternet;
  }

  // 503 Service Unavailable → maintenance (matches mobile exactly)
  if (error.status === 503) {
    return NetworkErrorType.Maintenance;
  }

  // 408 Request Timeout or 504 Gateway Timeout → timeout
  if (error.status === 408 || error.status === 504) {
    return NetworkErrorType.Timeout;
  }

  // Other 5xx: configurable (GTN uses false → Unknown)
  if (error.status >= 500 && treatServerErrorsAsTimeout) {
    return NetworkErrorType.Timeout;
  }

  return NetworkErrorType.Unknown;
}

/**
 * User-facing message for a network error type.
 *
 * Mobile reference: `GtnAppDioInterceptor._messageForNetworkError()`
 */
export function networkErrorMessage(type: NetworkErrorType): string {
  switch (type) {
    case NetworkErrorType.NoInternet:
      return 'No internet connection. Please check your network and try again.';
    case NetworkErrorType.Timeout:
      return 'The server is not responding. Please try again.';
    case NetworkErrorType.Maintenance:
      return 'The server is not responding. Please try again.';
    case NetworkErrorType.Unknown:
      return 'An unexpected error occurred. Please try again.';
  }
}

/**
 * Backend error type key for a network error.
 *
 * Mobile reference: `GtnAppDioInterceptor._backendErrorKeyForNetworkError()`
 */
export function networkErrorTypeKey(type: NetworkErrorType): string {
  switch (type) {
    case NetworkErrorType.NoInternet:
    case NetworkErrorType.Timeout:
    case NetworkErrorType.Maintenance:
      return 'ERROR_NETWORK';
    case NetworkErrorType.Unknown:
      return 'ERROR_UNKNOWN';
  }
}
